import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getEveningReports, createEveningReport, extractErrorMessage } from '../services/api';
import { FaPlus, FaMoon } from 'react-icons/fa';
import UserSelect from '../components/UserSelect';
import { useToast } from '../components/Toast';

function EveningReports({ user }) {
  const toast = useToast();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [newReport, setNewReport] = useState({
    user_id: '',
    mood: 5,
    energy: 5,
    highlights: '',
    problems: '',
    reflection: '',
    confession: '',
    gratitude: ''
  });

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const data = await getEveningReports();
      setReports(data.reports || data);
    } catch (error) {
      console.error('Error fetching evening reports:', error);
      toast.error(extractErrorMessage(error, 'Abendberichte konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setFormError(null);
    try {
      await createEveningReport(newReport);
      setNewReport({
        user_id: '', mood: 5, energy: 5, highlights: '',
        problems: '', reflection: '', confession: '', gratitude: ''
      });
      setShowModal(false);
      toast.success('Abendbericht erstellt');
      fetchReports();
    } catch (error) {
      console.error('Error creating evening report:', error);
      setFormError(extractErrorMessage(error, 'Fehler beim Erstellen des Abendberichts'));
    } finally {
      setSaving(false);
    }
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setFormError(null);
  };

  const getMoodEmoji = (mood) => {
    if (mood >= 8) return '😊';
    if (mood >= 6) return '🙂';
    if (mood >= 4) return '😐';
    if (mood >= 2) return '😔';
    return '😢';
  };

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1><FaMoon /> Evening Reports</h1>
          <button className="add-btn" onClick={() => setShowModal(true)}>
            <FaPlus /> New Evening Report
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Mood</th>
                  <th>Energy</th>
                  <th>Highlights</th>
                  <th>Problems</th>
                  <th>Reflection</th>
                  <th>Confession</th>
                  <th>Gratitude</th>
                </tr>
              </thead>
              <tbody>
                {reports.length > 0 ? (
                  reports.map(report => (
                    <tr key={report.id}>
                      <td>
                        {new Date(report.created_at).toLocaleDateString('de-DE', {
                          weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
                        })}
                      </td>
                      <td>{getMoodEmoji(report.mood)} {report.mood}/10</td>
                      <td>{report.energy}/10</td>
                      <td>{report.highlights || '-'}</td>
                      <td>{report.problems || '-'}</td>
                      <td>{report.reflection || '-'}</td>
                      <td>{report.confession || '-'}</td>
                      <td>{report.gratitude || '-'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="no-data">No evening reports found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>New Evening Report</h2>
              {formError && <div className="shared-form-error-banner">{formError}</div>}
              <form onSubmit={handleCreate}>
                <div className="form-group">
                  <UserSelect
                    value={newReport.user_id}
                    onChange={id => setNewReport({...newReport, user_id: id})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Mood: {newReport.mood}/10 {getMoodEmoji(newReport.mood)}</label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={newReport.mood}
                    onChange={e => setNewReport({...newReport, mood: parseInt(e.target.value)})}
                    className="slider"
                  />
                </div>
                <div className="form-group">
                  <label>Energy: {newReport.energy}/10</label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={newReport.energy}
                    onChange={e => setNewReport({...newReport, energy: parseInt(e.target.value)})}
                    className="slider"
                  />
                </div>
                <div className="form-group">
                  <label>Highlights</label>
                  <textarea
                    value={newReport.highlights}
                    onChange={e => setNewReport({...newReport, highlights: e.target.value})}
                    placeholder="What went well today?"
                  />
                </div>
                <div className="form-group">
                  <label>Problems</label>
                  <textarea
                    value={newReport.problems}
                    onChange={e => setNewReport({...newReport, problems: e.target.value})}
                    placeholder="Any issues or challenges?"
                  />
                </div>
                <div className="form-group">
                  <label>Reflection</label>
                  <textarea
                    value={newReport.reflection}
                    onChange={e => setNewReport({...newReport, reflection: e.target.value})}
                    placeholder="Reflect on your day..."
                  />
                </div>
                <div className="form-group">
                  <label>Confession</label>
                  <textarea
                    value={newReport.confession}
                    onChange={e => setNewReport({...newReport, confession: e.target.value})}
                    placeholder="Anything to confess?"
                  />
                </div>
                <div className="form-group">
                  <label>Gratitude</label>
                  <textarea
                    value={newReport.gratitude}
                    onChange={e => setNewReport({...newReport, gratitude: e.target.value})}
                    placeholder="What are you grateful for?"
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeModal} disabled={saving}>Cancel</button>
                  <button type="submit" className="submit-btn" disabled={saving}>{saving ? 'Speichert\u2026' : 'Submit'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default EveningReports;
